/* eslint complexity: ["error", {max: 10}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 327}] -- Due to the nature of the component */
/* eslint max-lines: ["error", {max: 388}] -- Due to the nature of the component */
/* eslint max-statements: ["error", {max: 35}] -- this applies for the entire file */
import { useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
} from "@/constants/analytics/Analytics";
import { ScheduleAnalyticsSources } from "@/constants/analytics/Scheduling";
import { BookingStepValue } from "@/constants/Booking";
import { EditAppointmentContent } from "@/constants/EditEvents";
import type { EditAppointmentScope } from "@/constants/EditEvents";
import { CTAText, EventCancellationError } from "@/constants/Schedule";
import { ConfirmationAlertTestID, ModalTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useBookingAnalytics, {
  RescheduleAppointmentEvent,
} from "@/hooks/useBookingAnalytics";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import { useConfirmAppointmentMutation } from "@/store/api/schedule/booking";
import {
  useCancelAppointmentWithRescheduledAppointmentMutation,
  useCancelAppointmentWithSubReasonMutation,
} from "@/store/api/schedule/schedule";
import { useAppUserSelector } from "@/store/reducers/appUser";
import RescheduleAvailabilityCard from "@/ui/components/schedule/booking/rescheduling/RescheduleAvailabilityCard";
import RescheduleOverviewCard from "@/ui/components/schedule/booking/rescheduling/RescheduleOverviewCard";
import CancelAppointmentModal from "@/ui/components/schedule/CancelAppointmentModal";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Button, FAIconName } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import {
  appointmentRecurrenceToFrequency,
  doesRecur,
  getAppointmentAttendees,
} from "@/utils/schedule.util";
import { joinTestIDs } from "@/utils/string.util";

enum EditAppointmentBaseContent {
  Description = "Please choose a new time slot for this ongoing series",
  DismissAccessibilityLabel = "Dismiss Edit Appointment Modal",
}

interface EditAppointmentBottomSheetModalProps {
  eventMetadata: AppointmentDetailResponse;
  eventTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onCompletion: () => void;
}

type EditAppointmentStep = "confirm" | "edit";

const EditAppointmentBottomSheetModal = ({
  eventMetadata,
  eventTitle,
  isOpen,
  onClose,
  onCompletion,
}: EditAppointmentBottomSheetModalProps) => {
  const { appointmentExternalId, patient, recurrence } = eventMetadata;
  const baseTestID = joinTestIDs(
    ModalTestID.EditAppointment,
    appointmentExternalId,
    ModalTestID.Modal,
  );
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);

  const [currentStep, setCurrentStep] = useState<EditAppointmentStep>("edit");
  const [editAppointmentScope, setEditAppointmentScope] =
    useState<EditAppointmentScope>("appointment");

  const { isPatient: isAppUserPatient } = useAppUserSelector();
  const patientExternalId = patient.externalId;
  const { trackRescheduleEvent } = useBookingAnalytics();

  const isRecurring = doesRecur(recurrence);
  const isOverviewStep = currentStep === "confirm";
  const isEditingSeries = editAppointmentScope === "series";

  const {
    cancelAppointmentAnalyticsEvent,
    cancelAppointmentButtonTitle,
    title,
    toggleModeButtonIcon,
    toggleModeButtonTitle,
  } = EditAppointmentContent[editAppointmentScope];

  const toggleEditMode = () => {
    setEditAppointmentScope(isEditingSeries ? "appointment" : "series");

    trackRescheduleEvent({
      event: RescheduleAppointmentEvent.RescheduleRecurrenceSelection,
      isEditingSeries,
      isRecurringEvent: isRecurring,
      source: BookingStepValue.availability,
    });
  };

  const { showToast } = useToast();
  const { track } = useAnalytics();

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<
    TimeSlot | undefined
  >(undefined);

  const [confirmAppointment, { isLoading: isConfirmAppointmentLoading }] =
    useConfirmAppointmentMutation();
  const [cancelAppointmentWithSubReason] =
    useCancelAppointmentWithSubReasonMutation();

  const [
    cancelAppointmentWithRescheduledAppointment,
    { isLoading: isCancelAppointmentLoading },
  ] = useCancelAppointmentWithRescheduledAppointmentMutation();

  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState<boolean>(false);
  const [cancellationSubReason, setCancellationSubReason] = useState<
    MultiSelectOption | undefined
  >(undefined);

  const handleCancelEventConfirm = () => {
    track(cancelAppointmentAnalyticsEvent, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.EventId]: appointmentExternalId,
      [AnalyticsFieldName.EventTitle]: eventTitle,
      [AnalyticsFieldName.Source]:
        ScheduleAnalyticsSources.AppointmentDetailView,
    });

    cancelAppointmentWithSubReason({
      appointmentExternalId,
      cancellationSubReason: cancellationSubReason?.value || "",
      patientExternalId,
      shouldCancelAll: isEditingSeries,
    })
      .then(() => onCompletion())
      .catch(() => {
        showToast({
          text: EventCancellationError.CancelingAppointmentError,
          variant: "error",
        });
        setIsCancelAlertOpen(false);
      });
  };

  const handleEditAppointmentModalDismiss = () => {
    trackRescheduleEvent({
      event: RescheduleAppointmentEvent.RescheduleLeaveBookingModal,
      isEditingSeries,
      isRecurringEvent: isRecurring,
      source:
        currentStep === "edit"
          ? BookingStepValue.availability
          : BookingStepValue.confirmation,
    });

    setSelectedTimeSlot(undefined);
    setCurrentStep("edit");
    setEditAppointmentScope("appointment");

    onClose();
  };

  const handleAlertDismiss = () => {
    setIsCancelAlertOpen(false);
  };

  const handleCancelEventPress = () => {
    setIsCancelAlertOpen(true);
  };

  const handleConfirmTimeSlotPress = () => {
    setCurrentStep("confirm");

    trackRescheduleEvent({
      event: RescheduleAppointmentEvent.RescheduleContinueCTAClicked,
      isEditingSeries,
      isRecurringEvent: isRecurring,
      source: BookingStepValue.availability,
    });
  };

  const handleBackPress = () => {
    setCurrentStep("edit");
  };

  const handleConfirmReschedulePress = () => {
    const toastContent = isEditingSeries
      ? "Your series has been successfully rescheduled."
      : "Your appointment has been successfully rescheduled.";

    if (isNil(selectedTimeSlot)) {
      return;
    }

    trackRescheduleEvent({
      event: RescheduleAppointmentEvent.RescheduleConfirmBooking,
      isEditingSeries,
      isRecurringEvent: isRecurring,
      source: BookingStepValue.confirmation,
    });

    const {
      appointmentTypeLabel: appointmentType,
      host: { externalId: hostExternalId },
      isPatientAttending,
      providerAttendees,
      supportAttendees,
    } = eventMetadata;
    confirmAppointment({
      appointmentEndDateTime: new Date(selectedTimeSlot.end).toISOString(),
      appointmentStartDateTime: new Date(selectedTimeSlot.start).toISOString(),
      appointmentType,
      hostExternalId,
      isPatientAttending,
      isRescheduleRequest: true,
      patientExternalId,
      providerAttendeeExternalIds: providerAttendees?.map(
        (attendee) => attendee.externalId,
      ),
      recurrenceFrequency: appointmentRecurrenceToFrequency(
        isEditingSeries ? recurrence : undefined,
      ),
      supportAttendeeExternalIds: supportAttendees?.map(
        (attendee) => attendee.externalId,
      ),
    })
      .unwrap()
      .then((appointment) => {
        cancelAppointmentWithRescheduledAppointment({
          appointmentExternalId,
          isAppUserPatient,
          patientExternalId,
          rescheduleAppointmentExternalId:
            appointment.data ?? appointmentExternalId,
          shouldCancelAll: isEditingSeries,
        })
          .unwrap()
          .then(() => {
            router.dismiss();
            showToast({ text: toastContent, variant: "success" });
          })
          .catch(() => {
            // Even if the cancellation fails, we want to navigate away from this screen
            // since the new appointment was created successfully
            showToast({ text: toastContent, variant: "success" });
            router.dismiss();
          });
      })
      .catch(() => {
        router.dismiss();
        showToast({
          text: "We were unable to reschedule your appointment. Please try again later.",
          variant: "error",
        });
      });
  };

  const isContinueButtonDisabled = isNil(selectedTimeSlot);

  const handleTimeSlotSelected = (timeSlot?: TimeSlot) => {
    setSelectedTimeSlot(timeSlot);
    trackRescheduleEvent({
      event: RescheduleAppointmentEvent.RescheduleAvailabilitySelection,
      isEditingSeries,
      isRecurringEvent: isRecurring,
      source: BookingStepValue.availability,
    });
  };

  const handleLoadingChange = (isListLoading: boolean) => {
    setIsAvailabilityLoading(isListLoading);
  };

  const appointmentAttendees = getAppointmentAttendees(eventMetadata);
  const eventRecurrence = appointmentRecurrenceToFrequency(recurrence);

  const isLoading =
    isConfirmAppointmentLoading ||
    isAvailabilityLoading ||
    isCancelAppointmentLoading;
  return (
    <BottomSheetModal
      dismissAccessibilityLabel={
        EditAppointmentBaseContent.DismissAccessibilityLabel
      }
      isOpen={isOpen}
      isScrollable={!isOverviewStep}
      onRequestClose={handleEditAppointmentModalDismiss}
      shouldResizeOnContentChange
      snapPoints={["70%"]}
      testID={baseTestID}
      title={title}
    >
      {/* flex: 1 needed for FlashList scroll area; omitted on overview (CSS flex-basis: 0% collapses in auto-height parents on web) */}
      <VStack
        flex={isOverviewStep ? undefined : 1}
        gap={12}
        paddingTop={isRecurring ? 18 : 0}
      >
        {!isOverviewStep && isRecurring && (
          <Button
            isFullWidth
            leftIcon={toggleModeButtonIcon}
            onPress={toggleEditMode}
            testID={joinTestIDs(baseTestID, "decline")}
            title={toggleModeButtonTitle}
            variant="secondary"
          />
        )}
        {isOverviewStep ? (
          <RescheduleOverviewCard
            attendees={appointmentAttendees}
            eventTitle={eventTitle}
            recurrence={isEditingSeries ? eventRecurrence : undefined}
            scope={editAppointmentScope}
            selectedTimeSlot={selectedTimeSlot}
            testID={baseTestID}
          />
        ) : (
          <RescheduleAvailabilityCard
            onLoadingChange={handleLoadingChange}
            onTimeSlotSelect={handleTimeSlotSelected}
            scope={editAppointmentScope}
          />
        )}
        <HStack justifyContent="space-between">
          {isOverviewStep ? (
            <>
              <Button
                leftIcon={FAIconName.ChevronLeft}
                onPress={handleBackPress}
                testID={joinTestIDs(baseTestID, "back")}
                title={CTAText.Back}
                variant="secondary"
              />
              <Button
                isLoading={isLoading}
                leftIcon={FAIconName.Check}
                onPress={handleConfirmReschedulePress}
                testID={joinTestIDs(baseTestID, "save-changes")}
                title={CTAText.SaveChanges}
                variant="primary"
              />
            </>
          ) : (
            <>
              <Button
                leftIcon={FAIconName.Trash}
                onPress={handleCancelEventPress}
                testID={joinTestIDs(baseTestID, "cancel-appointment")}
                title={cancelAppointmentButtonTitle}
                variant="tertiary.critical"
              />
              <Button
                isDisabled={isContinueButtonDisabled}
                isLoading={isLoading}
                onPress={handleConfirmTimeSlotPress}
                rightIcon={FAIconName.ChevronRight}
                testID={joinTestIDs(baseTestID, "continue")}
                title="Continue"
                variant="primary"
              />
            </>
          )}
        </HStack>
      </VStack>
      <CancelAppointmentModal
        cancellationReason={cancellationSubReason}
        editAppointmentScope={editAppointmentScope}
        isOpen={isCancelAlertOpen}
        onConfirm={handleCancelEventConfirm}
        onDismiss={handleAlertDismiss}
        setCancellationReason={setCancellationSubReason}
        testID={ConfirmationAlertTestID.CancelEvent}
      />
    </BottomSheetModal>
  );
};

export default EditAppointmentBottomSheetModal;
